import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { IMAGES } from "@/lib/images";

const initialsOf = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const TeamCard = ({ idx, name, role, image, testid }) => (
  <article data-testid={testid} className="group relative overflow-hidden">
    <div className="relative aspect-[3/4] overflow-hidden bg-[#111114]">
      {image ? (
        <>
          <img
            src={image}
            alt={name}
            className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-[1200ms]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="absolute inset-0 opacity-60"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at center, rgba(197,168,128,0.18), transparent 60%)",
            }}
          />
          <div className="absolute inset-0 opacity-30 mix-blend-overlay"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 2px, transparent 2px 10px)",
            }}
          />
          <div className="relative flex flex-col items-center justify-center text-center px-6">
            <span className="font-serif-display italic text-7xl md:text-8xl text-[var(--ps-gold)] leading-none">
              {initialsOf(name)}
            </span>
            <span className="mt-5 text-[10px] tracking-[0.32em] uppercase text-zinc-400">
              Portrait · Coming soon
            </span>
          </div>
          <div className="absolute inset-0 border border-white/5 pointer-events-none" />
        </div>
      )}
      <div className="absolute top-5 left-5 ps-number text-[var(--ps-gold)] text-xl z-10">
        0{idx}
      </div>
    </div>
    <div className="mt-6">
      <h3 className="font-serif-display text-2xl md:text-3xl text-white">{name}</h3>
      <p className="mt-2 text-xs tracking-[0.2em] uppercase text-zinc-400">{role}</p>
    </div>
  </article>
);

const Team = () => {
  const { t } = useLanguage();
  const { ref, visible } = useReveal();
  const images = [IMAGES.teamStefano, IMAGES.teamLucia, IMAGES.teamTech];

  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative py-28 md:py-40 border-t border-white/5 bg-[#0b0b0e]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col mb-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-6 h-[1px] bg-[var(--ps-gold)]" />
            <span className="text-xs tracking-[0.3em] text-[var(--ps-gold)]">
              {t.team.kicker}
            </span>
          </div>
          <h2
            data-testid="team-title"
            className="font-serif-display text-4xl md:text-6xl leading-[1.02] text-white max-w-3xl"
          >
            {t.team.title.split(" ").slice(0, 2).join(" ")}{" "}
            <span className="italic text-[var(--ps-gold)]">
              {t.team.title.split(" ").slice(2).join(" ")}
            </span>
          </h2>
        </div>

        <div
          ref={ref}
          className={`grid md:grid-cols-3 gap-10 md:gap-14 reveal-stagger ${
            visible ? "is-visible" : ""
          }`}
        >
          {t.team.members.map((m, i) => (
            <TeamCard
              key={i}
              idx={i + 1}
              name={m.name}
              role={m.role}
              image={images[i]}
              testid={`team-member-${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
